/**
 * Client-side GEO "citability" scorer (AB#243-B).
 *
 * Estimates how likely AI search engines are to cite a post/page (0-100) from
 * the editor's live content plus a couple of post fields. Pure TS, no deps.
 * The backend (wpp-core/src/ssg) computes an authoritative score at build time;
 * this is a fast in-editor proxy over the same dimensions and need not byte-match.
 *
 * Content arrives as Markdown (post editor / MDXEditor) OR HTML (page editor /
 * Tiptap), so extraction branches on format. Field factors (author, date) are
 * optional: pass `undefined` when the content type has no such field (pages) and
 * they are dropped from the weighting and the score renormalized to 0-100.
 */

export type FactorStatus = 'met' | 'partial' | 'missing';

export interface CitabilityFactor {
  key: string;
  label: string;
  weight: number;
  points: number;
  status: FactorStatus;
  /** Short human note, e.g. "312 words" or "Flesch 58". */
  detail: string;
  /** Fix + why-it-matters, present only when the factor is weak (not met). */
  suggestion?: string;
}

export interface CitabilityResult {
  score: number; // 0..100
  factors: CitabilityFactor[];
}

/** Structural signals pulled from the content, format-agnostic. */
export interface ContentSignals {
  words: number;
  /** Flesch Reading Ease, or null when there's too little text to assess. */
  fre: number | null;
  h2: number;
  h3: number;
  /** True if the heading outline jumps a level (e.g. H2 -> H4). */
  headingSkip: boolean;
  lists: number;
  /** Absolute http(s) links (treated as outbound). */
  outboundLinks: number;
}

/** Post fields; `undefined` = the content type has no such field (e.g. pages). */
export interface CitabilityFields {
  hasAuthor?: boolean;
  hasDate?: boolean;
}

/**
 * Factor weights (posts sum to 100). Tuned for AI-search citability: structure
 * and lists (extractable passages) and outbound citations weigh heavily; author
 * and date carry attribution/freshness signals.
 */
const WEIGHTS = {
  headings: 20,
  wordCount: 15,
  outboundLinks: 15,
  authorship: 15,
  publicationDate: 15,
  lists: 10,
  readingLevel: 10,
} as const;

// ---------------------------------------------------------------------------
// Reading level (Flesch Reading Ease)
// ---------------------------------------------------------------------------

/**
 * Naive English syllable count. ponytail: heuristic (vowel groups minus common
 * silent endings); good enough for a reading-ease proxy, swap for hyphenation
 * dict only if the score proves noisy.
 */
function countSyllables(word: string): number {
  const w = word.toLowerCase().replace(/[^a-z]/g, '');
  if (w.length === 0) return 0;
  if (w.length <= 3) return 1;
  const cleaned = w.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '').replace(/^y/, '');
  const groups = cleaned.match(/[aeiouy]{1,2}/g);
  return groups ? groups.length : 1;
}

/**
 * Flesch Reading Ease: 206.835 - 1.015*(words/sentence) - 84.6*(syllables/word).
 * Higher = easier to read. Returns null under ~30 words (sample too small to
 * mean anything). For GEO we favour clear, scannable prose (aim >= 50).
 */
export function fleschReadingEase(text: string): number | null {
  const words = text.split(/\s+/).filter((w) => /[a-z0-9]/i.test(w));
  if (words.length < 30) return null;
  const sentenceMatches = text.match(/[.!?]+(?=\s|$)/g);
  const sentences = Math.max(1, sentenceMatches ? sentenceMatches.length : 1);
  let syllables = 0;
  for (const w of words) syllables += countSyllables(w);
  const wordsPerSentence = words.length / sentences;
  const syllablesPerWord = syllables / words.length;
  return 206.835 - 1.015 * wordsPerSentence - 84.6 * syllablesPerWord;
}

// ---------------------------------------------------------------------------
// Signal extraction (Markdown or HTML)
// ---------------------------------------------------------------------------

function markdownToText(md: string): string {
  return md
    .replace(/```[\s\S]*?```/g, ' ') // fenced code
    .replace(/`[^`]*`/g, ' ') // inline code
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ') // images
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // links -> text
    .replace(/^>+\s?/gm, '') // blockquotes
    .replace(/^#{1,6}\s+/gm, '') // heading marks
    .replace(/^\s*([-*+]|\d+[.)])\s+/gm, '') // list markers
    .replace(/[*_~]/g, '') // emphasis marks
    .replace(/<[^>]+>/g, ' ') // stray html
    .replace(/\s+/g, ' ')
    .trim();
}

export function buildSignals(
  text: string,
  levels: number[],
  h2: number,
  h3: number,
  lists: number,
  outboundLinks: number,
): ContentSignals {
  const words = text ? text.split(/\s+/).filter(Boolean).length : 0;
  let headingSkip = false;
  for (let i = 1; i < levels.length; i++) {
    if (levels[i] > levels[i - 1] + 1) {
      headingSkip = true;
      break;
    }
  }
  return { words, fre: fleschReadingEase(text), h2, h3, headingSkip, lists, outboundLinks };
}

export function markdownCitabilitySignals(md: string): ContentSignals {
  const levels: number[] = [];
  let lists = 0;
  let inList = false;
  let inFence = false;
  for (const line of md.split('\n')) {
    if (/^\s*```/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const heading = /^(#{1,6})\s+\S/.exec(line);
    if (heading) levels.push(heading[1].length);
    const isListItem = /^\s*([-*+]|\d+[.)])\s+\S/.test(line);
    if (isListItem && !inList) lists += 1;
    inList = isListItem;
  }
  const h2 = levels.filter((l) => l === 2).length;
  const h3 = levels.filter((l) => l === 3).length;
  // Markdown links to http(s); negative lookbehind excludes images (![...]).
  const outboundLinks = (md.match(/(?<!!)\[[^\]]*\]\(https?:\/\/[^)\s]+/g) || []).length;
  return buildSignals(markdownToText(md), levels, h2, h3, lists, outboundLinks);
}

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

function points(weight: number, status: FactorStatus): number {
  if (status === 'met') return weight;
  if (status === 'partial') return Math.round(weight / 2);
  return 0;
}

function factor(
  key: string,
  label: string,
  weight: number,
  status: FactorStatus,
  detail: string,
  suggestion: string,
): CitabilityFactor {
  return {
    key,
    label,
    weight,
    status,
    points: points(weight, status),
    detail,
    suggestion: status === 'met' ? undefined : suggestion,
  };
}

export function scoreFromSignals(s: ContentSignals, fields: CitabilityFields): CitabilityResult {
  const factors: CitabilityFactor[] = [];

  // Heading structure
  let headingStatus: FactorStatus;
  if (s.h2 === 0 && s.h3 === 0) headingStatus = 'missing';
  else if (s.h2 === 0 || s.headingSkip) headingStatus = 'partial';
  else headingStatus = 'met';
  factors.push(
    factor(
      'headings',
      'Heading structure',
      WEIGHTS.headings,
      headingStatus,
      `${s.h2} H2 / ${s.h3} H3${s.headingSkip ? ', level skip' : ''}`,
      headingStatus === 'missing'
        ? 'Add H2/H3 subheadings. AI engines pull answers section by section, so clear headings make passages easy to cite.'
        : 'Lead with H2s and don’t skip levels. A clean outline helps AI locate quotable sections.',
    ),
  );

  // Word count
  const wordStatus: FactorStatus = s.words >= 300 ? 'met' : s.words >= 150 ? 'partial' : 'missing';
  factors.push(
    factor(
      'wordCount',
      'Word count',
      WEIGHTS.wordCount,
      wordStatus,
      `${s.words} words`,
      'Aim for 300+ words. Thin content is rarely cited as an authoritative source.',
    ),
  );

  // Outbound links
  const linkStatus: FactorStatus =
    s.outboundLinks >= 2 ? 'met' : s.outboundLinks === 1 ? 'partial' : 'missing';
  factors.push(
    factor(
      'outboundLinks',
      'Outbound links',
      WEIGHTS.outboundLinks,
      linkStatus,
      `${s.outboundLinks} external`,
      'Cite 1–2 reputable sources with outbound links. Citations signal trustworthiness to AI search.',
    ),
  );

  // Authorship (field factor; skipped when not applicable)
  if (fields.hasAuthor !== undefined) {
    factors.push(
      factor(
        'authorship',
        'Author / byline',
        WEIGHTS.authorship,
        fields.hasAuthor ? 'met' : 'missing',
        fields.hasAuthor ? 'set' : 'none',
        'Set an author. AI engines weight authored, attributable content more highly.',
      ),
    );
  }

  // Publication date (field factor; skipped when not applicable)
  if (fields.hasDate !== undefined) {
    factors.push(
      factor(
        'publicationDate',
        'Publication date',
        WEIGHTS.publicationDate,
        fields.hasDate ? 'met' : 'missing',
        fields.hasDate ? 'set' : 'none',
        'Publish or set a date. A visible date signals freshness to AI search.',
      ),
    );
  }

  // List usage
  const listStatus: FactorStatus = s.lists >= 1 ? 'met' : 'missing';
  factors.push(
    factor(
      'lists',
      'List usage',
      WEIGHTS.lists,
      listStatus,
      `${s.lists} list${s.lists === 1 ? '' : 's'}`,
      'Add a bulleted or numbered list. Lists are the format AI answers extract most often.',
    ),
  );

  // Reading level
  let readStatus: FactorStatus;
  if (s.fre === null) readStatus = 'missing';
  else if (s.fre >= 50) readStatus = 'met';
  else if (s.fre >= 30) readStatus = 'partial';
  else readStatus = 'missing';
  factors.push(
    factor(
      'readingLevel',
      'Reading level',
      WEIGHTS.readingLevel,
      readStatus,
      s.fre === null ? 'not enough text' : `Flesch ${Math.round(s.fre)}`,
      'Shorten sentences and simplify wording. Clearer prose (higher Flesch ease) is easier for AI to quote accurately.',
    ),
  );

  const applicable = factors.reduce((sum, f) => sum + f.weight, 0);
  const earned = factors.reduce((sum, f) => sum + f.points, 0);
  const score = applicable === 0 ? 0 : Math.round((earned / applicable) * 100);
  return { score, factors };
}
